import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useAdminLoad from "@/hooks/useAdminLoad";
import { useAdmin } from "@/admin/AuthContext";
import { Empty, LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import GenderSplit, { genderLabel } from "@/components/admin/launch/GenderSplit";
import ReleaseBatch from "@/components/admin/launch/ReleaseBatch";
import { api, downloadUrl } from "@/lib/api";
import { GENDERS } from "@/lib/constants";
import { formatDate, humanize } from "@/lib/format";

const PAGE = 100;
const EMPTY = { q: "", zip: "", gender: "all", source: "", status: "all", is_test: "all", since: "", until: "" };
const STATUSES = ["waiting", "in_wave", "invited", "released", "signed_up"];
const STATUS_TONE = { signed_up: "success", invited: "brand", released: "brand" };
const NOTIFY = { pending: "Notice pending", held_quiet: "Held for quiet hours", sent: "Notified", failed: "Notice failed" };
const CHANNEL = { push: "push", sms: "text", email: "email" };
const canRelease = (w) => !w.released_at && !["released", "signed_up"].includes(w.invite_status);

function Released({ w }) {
  if (!w.released_at) return <span className="cw-muted">—</span>;
  const n = NOTIFY[w.notify_status] || w.notify_status;
  return (
    <div className="whitespace-nowrap" data-testid={`waitlist-released-${w.id}`}>
      <p>{formatDate(w.released_at)}</p>
      {n && <p className={`text-xs ${w.notify_status === "failed" ? "cw-error" : "cw-muted"}`} data-testid={`waitlist-notify-${w.id}`}>{n}{w.notify_channel ? ` · ${CHANNEL[w.notify_channel] || w.notify_channel}` : ""}</p>}
    </div>
  );
}

function Pick({ id, label, value, onChange, options, testId }) {
  return (
    <div><Label htmlFor={id} className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-1 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All</SelectItem>{options.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${testId}-option-${k}`}>{l}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function Filters({ form, setForm, onApply, onClear }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onApply(); }} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end" data-testid="waitlist-filters">
      <div><Label htmlFor="wl-q" className="text-xs">Email contains</Label><Input id="wl-q" value={form.q} onChange={set("q")} className="mt-1 bg-white" data-testid="waitlist-filter-q-input" /></div>
      <div><Label htmlFor="wl-zip" className="text-xs">Postal codes</Label><Input id="wl-zip" value={form.zip} onChange={set("zip")} placeholder="53202, K1A 0B6" className="mt-1 bg-white" data-testid="waitlist-filter-zip-input" /></div>
      <Pick id="wl-gender" label="Gender" value={form.gender} onChange={set("gender")} options={GENDERS.map((g) => [g.key, g.label])} testId="waitlist-filter-gender-select" />
      <div><Label htmlFor="wl-source" className="text-xs">Source</Label><Input id="wl-source" value={form.source} onChange={set("source")} placeholder="website" className="mt-1 bg-white" data-testid="waitlist-filter-source-input" /></div>
      <Pick id="wl-status" label="Status" value={form.status} onChange={set("status")} options={STATUSES.map((s) => [s, humanize(s)])} testId="waitlist-filter-status-select" />
      <Pick id="wl-test" label="Addresses" value={form.is_test} onChange={set("is_test")} options={[["false", "Real"], ["true", "Test"]]} testId="waitlist-filter-test-select" />
      <div><Label htmlFor="wl-since" className="text-xs">From</Label><Input id="wl-since" type="date" value={form.since} onChange={set("since")} className="mt-1 bg-white" data-testid="waitlist-filter-since-input" /></div>
      <div><Label htmlFor="wl-until" className="text-xs">To</Label><Input id="wl-until" type="date" value={form.until} onChange={set("until")} className="mt-1 bg-white" data-testid="waitlist-filter-until-input" /></div>
      <div className="flex gap-2"><Button type="submit" data-testid="waitlist-filter-apply-button">Apply</Button>
        <Button type="button" variant="ghost" onClick={onClear} data-testid="waitlist-filter-clear-button">Clear</Button></div>
    </form>
  );
}

function Counts({ data }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <Panel className="p-5" data-testid="waitlist-by-gender">
        <div className="mb-3 flex items-baseline justify-between gap-3"><p className="text-sm font-semibold">Gender split</p>
          <p className="cw-muted text-xs tabular-nums" data-testid="waitlist-test-total">{data.total} shown · {data.test_total ?? 0} test addresses</p></div><GenderSplit items={data.by_gender} testId="waitlist-gender-split" /></Panel>
      <Panel className="p-5" data-testid="waitlist-by-zip"><p className="mb-3 text-sm font-semibold">By postal code</p>
        <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">{data.by_zip.map((z) => <Pill key={z.zip} className="tabular-nums" data-testid={`waitlist-zip-count-${z.zip}`}>{z.zip} · {z.count}</Pill>)}</div></Panel>
    </div>
  );
}

const toParams = (f) => ({ ...f, since: f.since && new Date(`${f.since}T00:00:00`).toISOString(), until: f.until && new Date(`${f.until}T23:59:59`).toISOString() });

export default function WaitlistTab() {
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({ ...EMPTY, skip: 0 });
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const owner = useAdmin().admin?.role === "owner";
  const p = toParams(filters);
  const qs = new URLSearchParams({ limit: String(PAGE), skip: String(filters.skip) });
  Object.entries(p).forEach(([k, v]) => k !== "skip" && v && v !== "all" && qs.set(k, v));
  const { data, error, loading, reload } = useAdminLoad(`/admin/waitlist?${qs}`);
  const total = data?.total ?? 0;
  const ids = data?.items?.map((w) => w.id) || [];
  const toggle = (id, on) => setSelected((s) => (on ? [...s, id] : s.filter((x) => x !== id)));

  const del = async () => {
    const res = deleting.length === 1 ? await api(`/admin/waitlist/${deleting[0]}`, { method: "DELETE" }) : await api("/admin/waitlist/delete", { body: { ids: deleting } });
    toast.success(`Removed ${res.deleted} ${res.deleted === 1 ? "entry" : "entries"} from the waitlist.`);
    setSelected((s) => s.filter((x) => !deleting.includes(x)));
    reload();
  };
  const release = async () => {
    try {
      await api(`/admin/waitlist/${releasing.id}/release`, { method: "POST" });
      toast.success(`${releasing.email} released.`);
    } catch (e) {
      if (e.code !== "already_released") throw e;
      toast.info("Already released");
    }
    reload();
  };

  return (
    <div className="space-y-6" data-testid="launch-waitlist-tab">
      {owner && <ReleaseBatch onReleased={reload} />}
      <Filters form={form} setForm={setForm} onApply={() => { setFilters({ ...form, skip: 0 }); setSelected([]); }} onClear={() => { setForm(EMPTY); setFilters({ ...EMPTY, skip: 0 }); setSelected([]); }} />
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        {data && <Counts data={data} />}
        <Panel className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
            <p className="cw-muted text-xs" data-testid="waitlist-total">{total} {total === 1 ? "entry" : "entries"}{selected.length ? ` · ${selected.length} selected` : ""}</p>
            <div className="flex gap-2">
              {selected.length > 0 && <Button size="sm" variant="outline" className="cw-error" onClick={() => setDeleting(selected)} data-testid="waitlist-bulk-delete-button"><Trash />Remove selected from waitlist</Button>}
              <Button size="sm" variant="outline" asChild><a href={downloadUrl("/admin/waitlist/export.csv", { zip: filters.zip, gender: filters.gender, source: filters.source })} data-testid="waitlist-export-csv-link"><DownloadSimple />Export CSV</a></Button>
            </div>
          </div>
          {data?.items?.length ? (
            <div className="overflow-x-auto"><table className="w-full min-w-[980px] text-left text-sm" data-testid="waitlist-table">
              <thead><tr className="cw-muted border-b border-border text-xs">
                <th className="w-12 py-3 pl-5"><Checkbox aria-label="Select all on this page" checked={ids.length > 0 && ids.every((i) => selected.includes(i))} onCheckedChange={(v) => setSelected(v ? [...new Set([...selected, ...ids])] : selected.filter((x) => !ids.includes(x)))} data-testid="waitlist-select-all" /></th>
                <th className="py-3 pr-4 font-medium">Email</th><th className="py-3 pr-4 font-medium">Postal code</th><th className="py-3 pr-4 font-medium">Gender</th><th className="py-3 pr-4 font-medium">Joined · source</th><th className="py-3 pr-4 font-medium">Status</th><th className="py-3 pr-4 font-medium">Released</th><th /></tr></thead>
              <tbody>{data.items.map((w) => (
                <tr key={w.id} className="border-b border-border last:border-0" data-testid={`waitlist-row-${w.id}`}>
                  <td className="py-3 pl-5"><Checkbox aria-label={`Select ${w.email}`} checked={selected.includes(w.id)} onCheckedChange={(v) => toggle(w.id, !!v)} data-testid={`waitlist-select-${w.id}`} /></td>
                  <td className="py-3 pr-4 font-medium">{w.email}{w.is_test && <Pill className="ml-2" data-testid={`waitlist-test-badge-${w.id}`}>Test</Pill>}</td>
                  <td className="py-3 pr-4 tabular-nums" title={[w.city, w.state].filter(Boolean).join(", ") || undefined}>{w.zip}{w.country ? ` · ${w.country}` : ""}</td>
                  <td className="py-3 pr-4">{genderLabel(w.gender)}</td>
                  <td className="cw-muted whitespace-nowrap py-3 pr-4"><p>{formatDate(w.created_at)}</p><p className="font-mono text-xs" data-testid={`waitlist-source-${w.id}`}>{w.source || "website"}</p></td>
                  <td className="py-3 pr-4"><Pill tone={STATUS_TONE[w.invite_status] || "neutral"} data-testid={`waitlist-status-${w.id}`}>{humanize(w.invite_status || "waiting")}</Pill></td>
                  <td className="py-3 pr-4"><Released w={w} /></td>
                  <td className="pr-3"><div className="flex items-center justify-end gap-1">
                    {owner && canRelease(w) && <Button size="sm" variant="outline" onClick={() => setReleasing(w)} data-testid={`waitlist-release-button-${w.id}`}><PaperPlaneTilt />Release</Button>}
                    <Button size="sm" variant="ghost" className="cw-error whitespace-nowrap" aria-label={`Remove ${w.email} from waitlist`} onClick={() => setDeleting([w.id])} data-testid={`waitlist-delete-button-${w.id}`}><Trash />Remove from waitlist</Button>
                  </div></td>
                </tr>))}</tbody>
            </table></div>
          ) : <Empty title="No one matches" text="Try widening the filters." testId="waitlist-empty" />}
          {total > PAGE && (
            <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm">
              <span className="cw-muted">{filters.skip + 1}–{Math.min(filters.skip + PAGE, total)} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={filters.skip === 0} onClick={() => setFilters((f) => ({ ...f, skip: f.skip - PAGE }))} data-testid="waitlist-prev-button">Previous</Button>
                <Button size="sm" variant="outline" disabled={filters.skip + PAGE >= total} onClick={() => setFilters((f) => ({ ...f, skip: f.skip + PAGE }))} data-testid="waitlist-next-button">Next</Button>
              </div>
            </div>
          )}
        </Panel>
      </LoadState>
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testId="waitlist-delete-confirm-dialog" onConfirm={del}
        title={deleting?.length === 1 ? "Remove from waitlist?" : `Remove ${deleting?.length} entries from the waitlist?`}
        confirmLabel="Remove from waitlist" description="This removes only the waitlist entry. Their account, if any, is not affected." />
      <ConfirmDialog open={!!releasing} onOpenChange={(o) => !o && setReleasing(null)} destructive={false} testId="waitlist-release-confirm-dialog" onConfirm={release}
        title={`Release ${releasing?.email}?`} confirmLabel="Release"
        description="They can sign up right away and get a “you’re in” notice by push, text or email. This can’t be undone." />
    </div>
  );
}
